import {
  BadgeCheck,
  BookOpen,
  Bell,
  CalendarCheck,
  CalendarDays,
  Crown,
  DoorOpen,
  LayoutGrid,
  Mail,
  Megaphone,
  UserRound,
  X,
} from "lucide-react";
import { MODULES_REGISTRY } from "@/core/navigation/modulesRegistry";
import type { IlustracaoLiderancaId } from "@/core/ajuda/licoes";
import {
  CabecalhoCartao,
  Cartao,
  DESTAQUE,
  ItemAviso,
  Linha,
  LinkFalso,
  Marcador,
  MiniBotao,
  Moldura,
  Pilha,
  Pilula,
  Poderes,
  SeloFuncao,
  SeloGestor,
  SeloLider,
  Sub,
  Campo,
  Texto,
} from "@/core/components/ajuda/ilustracaoPecas";

/**
 * Ilustrações das lições "Meu ministério", "Liderar um ministério" e "Gestor de elo". Desenhos
 * estáticos, com os mesmos textos e selos das telas reais (Início e tela do ministério).
 */

function CartaoConvite({ destacarAceitar = false }: { destacarAceitar?: boolean }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-primary/40 bg-primary/5 p-3">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <Mail size={20} />
        </span>
        <p className="m-0 min-w-0 flex-1 text-[13px] leading-snug text-text">
          Você foi convidado(a) para o ministério <strong>Biblioteca</strong> como{" "}
          <strong>Membro</strong>.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <MiniBotao rotulo="Aceitar" destacado={destacarAceitar} />
        <MiniBotao rotulo="Recusar" secundario />
      </div>
    </div>
  );
}

function CartaoElo({ destacarQuem = false }: { destacarQuem?: boolean }) {
  return (
    <Cartao folga="p-3.5">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <LayoutGrid size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-extrabold text-text">Elo sob responsabilidade</span>
          <span className="mt-1 inline-block">
            <Pilula>Biblioteca</Pilula>
          </span>
          <p className="m-0 mt-2 text-[13px] text-text">
            <span className={`inline-block rounded-md ${destacarQuem ? `px-1 ${DESTAQUE}` : ""}`}>
              <span className="text-muted">Quem gerencia: </span>
              <strong>Ana, Carlos</strong>
            </span>
          </p>
        </div>
      </div>
    </Cartao>
  );
}

function FormularioNovaFuncao({ marcarGestor }: { marcarGestor: boolean }) {
  return (
    <Cartao>
      <CabecalhoCartao
        titulo="Nova função"
        direita={<X size={16} className="shrink-0 text-muted" />}
      />
      <Campo rotulo="Nome da função" valor="Ex.: Líder, Gestor, Guitarrista" />
      <Marcador
        titulo="Lidera o ministério"
        apoio="Quem tem esta função convida e remove integrantes e define as funções do ministério."
      />
      <Marcador
        titulo="Gestor de elo"
        apoio="Quem tem esta função gerencia os elos sob responsabilidade deste ministério (ex.: aprovar empréstimos da Biblioteca)."
        marcada={marcarGestor}
        destacada={marcarGestor}
      />
      <div className="mt-3">
        <MiniBotao rotulo="Criar função" cheio />
      </div>
    </Cartao>
  );
}

function ListaIntegrantes({
  convidar = false,
  pendente = false,
}: {
  convidar?: boolean;
  pendente?: boolean;
}) {
  return (
    <Cartao>
      <CabecalhoCartao titulo="Integrantes" />
      <Sub>Convite: a pessoa aceita ou recusa.</Sub>
      <div className="my-2.5">
        <MiniBotao rotulo="Convidar" icone={Mail} destacado={convidar} />
      </div>
      <Linha
        nome="Ana"
        apoio={
          <>
            <SeloLider />
            <SeloFuncao texto="Membro" />
          </>
        }
      />
      <Linha
        nome={
          <span className="flex flex-wrap items-center gap-1.5">
            Bruno
            {pendente && <Pilula tom="espera">Convite pendente</Pilula>}
          </span>
        }
        apoio={<SeloFuncao texto="Membro" />}
      />
    </Cartao>
  );
}

function CartaoMeuMinisterio() {
  return (
    <Cartao folga="p-3.5">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-warning/10 text-warning">
          <Crown size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-warning">
            Meu ministério
          </span>
          <span className="block text-sm font-bold text-text">Biblioteca</span>
          <p className="m-0 mt-1 text-xs text-muted">
            Você lidera este ministério: convide integrantes e defina funções.
          </p>
          <div className="mt-2.5">
            <LinkFalso rotulo="Gerenciar" destacado />
          </div>
        </div>
      </div>
    </Cartao>
  );
}

export function IlustracaoLideranca({ id }: { id: IlustracaoLiderancaId }) {
  switch (id) {
    case "convite":
      return (
        <Moldura descricao="Cartão de convite no Início: “Você foi convidado(a) para o ministério Biblioteca como Membro”, com os botões Aceitar e Recusar.">
          <CartaoConvite />
        </Moldura>
      );

    case "convite-acao":
      return (
        <Moldura descricao="O mesmo cartão de convite, com o botão Aceitar em destaque ao lado do botão Recusar.">
          <CartaoConvite destacarAceitar />
        </Moldura>
      );

    case "funcoes":
      return (
        <Moldura descricao="Lista de integrantes de um ministério com a função de cada um: Ana é Líder do Ministério e Membro, Bruno é Secretário, Carla é Membro.">
          <Cartao>
            <CabecalhoCartao titulo="Integrantes de Biblioteca" />
            <Linha
              nome="Ana"
              apoio={
                <>
                  <SeloLider />
                  <SeloFuncao texto="Membro" />
                </>
              }
            />
            <Linha nome="Bruno" apoio={<SeloFuncao texto="Secretário" />} />
            <Linha nome="Carla" apoio={<SeloFuncao texto="Membro" />} />
          </Cartao>
        </Moldura>
      );

    case "avisos-funcao":
      return (
        <Moldura descricao="Dois avisos no sino: “Nova função em Biblioteca. Você agora tem a função Secretário. Definido por Ana.” e “Função retirada. Você deixou a função Secretário em Biblioteca.”">
          <Pilha>
            <ItemAviso
              icone={Bell}
              texto="Nova função em Biblioteca"
              quando="Você agora tem a função Secretário. Definido por Ana."
            />
            <ItemAviso
              icone={Bell}
              texto="Função retirada"
              quando="Você deixou a função Secretário em Biblioteca."
              lido
            />
          </Pilha>
        </Moldura>
      );

    case "meu-ministerio":
      return (
        <Moldura descricao="Cartão “Meu ministério” no Início, com o nome Biblioteca, o texto “Você lidera este ministério: convide integrantes e defina funções.” e o botão Gerenciar em destaque.">
          <CartaoMeuMinisterio />
        </Moldura>
      );

    case "selo-lider":
      return (
        <Moldura descricao="Dois nomes de integrantes: Ana com o selo Líder do Ministério, em destaque, e Carlos com o selo Gestor de elo.">
          <Cartao>
            <Linha nome="Ana" apoio={<SeloLider destacado />} />
            <Linha nome="Carlos" apoio={<SeloGestor />} />
          </Cartao>
        </Moldura>
      );

    case "nova-funcao":
      return (
        <Moldura descricao="Tela de funções do ministério com o botão + Nova função em destaque e, abaixo, a janela Nova função com o nome e as duas opções: Lidera o ministério e Gestor de elo.">
          <Pilha>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-extrabold text-text">Funções ministeriais</span>
              <MiniBotao rotulo="+ Nova função" destacado />
            </div>
            <FormularioNovaFuncao marcarGestor={false} />
          </Pilha>
        </Moldura>
      );

    case "nova-funcao-gestor":
      return (
        <Moldura descricao="Janela Nova função, com a opção Gestor de elo marcada e em destaque.">
          <FormularioNovaFuncao marcarGestor />
        </Moldura>
      );

    case "convidar":
      return (
        <Moldura descricao="Lista de integrantes com o botão Convidar em destaque. Bruno aparece com a etiqueta Convite pendente.">
          <ListaIntegrantes convidar pendente />
        </Moldura>
      );

    case "dar-funcao":
      return (
        <Moldura descricao="Primeiro, “+ Função” ao lado do nome de Bruno, em destaque. Depois, a janela “Dar função a Bruno”: Secretário entra na hora; Líder do Ministério pede aceite.">
          <Pilha>
            <Cartao>
              <Linha
                nome="Bruno"
                apoio={
                  <>
                    <SeloFuncao texto="Membro" />
                    <span
                      className={`inline-flex items-center gap-0.5 rounded-full border border-dashed border-primary px-2 py-0.5 text-[10px] font-bold text-primary dark:border-text dark:text-text ${DESTAQUE}`}
                    >
                      + Função
                    </span>
                  </>
                }
              />
            </Cartao>
            <Cartao>
              <CabecalhoCartao
                titulo="Dar função a Bruno"
                direita={<X size={16} className="shrink-0 text-muted" />}
              />
              <Texto pequeno>
                Já é integrante de Biblioteca, então funções comuns entram na hora e a pessoa é
                avisada. Líder do Ministério e Gestor de elo pedem o aceite dela.
              </Texto>
              <div className="mt-2 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-bg px-2.5 py-2">
                  <span className="text-xs font-semibold text-text">Secretário</span>
                  <Pilula>Entra na hora</Pilula>
                </div>
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-bg px-2.5 py-2">
                  <span className="text-xs font-semibold text-text">Líder do Ministério</span>
                  <Pilula tom="espera">Pede aceite</Pilula>
                </div>
              </div>
            </Cartao>
          </Pilha>
        </Moldura>
      );

    case "aviso-convite-resposta":
      return (
        <Moldura descricao="Dois avisos no sino: “Convite aceito. Bruno aceitou o convite para a função Secretário em Biblioteca.” e “Convite recusado. Carla recusou o convite para a função Líder do Ministério em Biblioteca.”">
          <Pilha>
            <ItemAviso
              icone={Bell}
              texto="Convite aceito"
              quando="Bruno aceitou o convite para a função Secretário em Biblioteca."
            />
            <ItemAviso
              icone={Bell}
              texto="Convite recusado"
              quando="Carla recusou o convite para a função Líder do Ministério em Biblioteca."
              lido
            />
          </Pilha>
        </Moldura>
      );

    case "elo":
      return (
        <Moldura descricao="Cartão “Elo sob responsabilidade” com a etiqueta Biblioteca e a linha “Quem gerencia: Ana, Carlos”.">
          <CartaoElo />
        </Moldura>
      );

    case "elo-quem":
      return (
        <Moldura descricao="Cartão “Elo sob responsabilidade” com a etiqueta Biblioteca e a linha “Quem gerencia: Ana, Carlos” em destaque.">
          <CartaoElo destacarQuem />
        </Moldura>
      );

    case "comparar-lider-gestor":
      return (
        <Moldura descricao="Quadro com duas colunas. Líder do Ministério: convida e remove integrantes, cria funções e gerencia os elos do ministério. Gestor de elo: gerencia os elos do ministério, mas não convida nem remove integrantes.">
          <div className="grid grid-cols-2 gap-2">
            <Cartao>
              <SeloLider />
              <ul className="m-0 mt-2 list-disc pl-4 text-[11.5px] leading-snug text-text">
                <li>Convida e remove integrantes</li>
                <li>Cria as funções</li>
                <li>Gerencia os elos do ministério</li>
              </ul>
            </Cartao>
            <Cartao>
              <SeloGestor />
              <ul className="m-0 mt-2 list-disc pl-4 text-[11.5px] leading-snug text-text">
                <li>Gerencia os elos do ministério</li>
                <li>Não convida nem remove integrantes</li>
              </ul>
            </Cartao>
          </div>
        </Moldura>
      );

    case "selo-gestor":
      return (
        <Moldura descricao="Lista de integrantes. Ana tem o selo Líder do Ministério. Carlos tem o selo Gestor de elo, em destaque. Bruno tem só a função Secretário.">
          <Cartao>
            <Linha nome="Ana" apoio={<SeloLider />} />
            <Linha nome="Carlos" apoio={<SeloGestor funcao="Bibliotecário" destacado />} />
            <Linha nome="Bruno" apoio={<SeloFuncao texto="Secretário" />} />
          </Cartao>
        </Moldura>
      );

    case "cartao-gestor":
      return (
        <Moldura descricao="Cartão do Início chamado Elos que você gerencia, com o selo Gestor de elo, o elo Biblioteca e o link Ver como usar.">
          <Cartao folga="p-3.5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-info/10 text-info dark:bg-info/30 dark:text-text">
                <BadgeCheck size={20} />
              </span>
              <div className="min-w-0 flex-1">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-info dark:text-text">
                  Elos que você gerencia
                </span>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Pilula tom="info">Biblioteca</Pilula>
                </div>
                <p className="m-0 mt-1.5 text-xs text-muted">
                  Você é Gestor de elo: cuide do que é do seu ministério.
                </p>
                <div className="mt-2.5">
                  <LinkFalso rotulo="Ver como usar" destacado />
                </div>
              </div>
            </div>
          </Cartao>
        </Moldura>
      );

    case "lista-elos": {
      const elos = [
        { codigo: "escala", icone: CalendarCheck, apoio: "Criar escalas e montar equipes." },
        { codigo: "biblioteca", icone: BookOpen, apoio: "Cuidar do acervo e dos empréstimos." },
        { codigo: "eventos", icone: CalendarDays, apoio: "Criar e aprovar eventos." },
        { codigo: "reservas", icone: DoorOpen, apoio: "Aprovar pedidos de salas e ambientes." },
        { codigo: "comunicados", icone: Megaphone, apoio: "Publicar avisos para a igreja." },
        { codigo: "visitantes", icone: UserRound, apoio: "Acompanhar quem chega." },
      ];
      return (
        <Moldura descricao="Os seis elos e o que se faz em cada um: Escala, Biblioteca, Eventos, Reservas, Comunicados e Visitantes.">
          <Poderes
            itens={elos.map(({ codigo, icone, apoio }) => ({
              icone,
              // O nome vem do registro dos elos, o mesmo do menu.
              titulo: MODULES_REGISTRY.find((m) => m.code === codigo)?.label ?? codigo,
              apoio,
            }))}
          />
        </Moldura>
      );
    }
  }
}
